import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

interface ComplexItem {
  id: number;
  name: string;
}
const dropdownData: ComplexItem[] = [
  { id: 1, name: 'Potato' },
  { id: 2, name: 'Onion' },
  { id: 3, name: 'Other' },
];

type ErrorState = 'warning' | 'error';

@Component({
  selector: 'lib-static-dropdown',
  templateUrl: './static-dropdown.component.html',
  styleUrls: ['./static-dropdown.component.scss'],
  imports: [CommonModule, ReactiveFormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StaticDropdownComponent implements OnInit, OnChanges {
  // Inputs
  @Input() appearance: 'fill' | 'standard' | 'outline' | 'legacy' = 'fill';
  @Input() propertyCodes: any;
  @Input() isSubmitted = false;
  @Input() useDifferentSet = false;
  @Input() dropdownDataList = [];
  @Input() type: 'full' | 'half' = 'full';
  @Input() selectedValue = '';
  @Input() labelName = 'Product Type';
  @Input() fullWidth = true;
  @Input() required = false;
  @Input() showErrors = true;
  @Input() formControlData: any;
  @Input() disabled = false;
  @Input() pageName = '';
  @Input() moveTypeName = '';
  @Input() commodityCategory = '';
  @Input() showGetData = false;
  @Input() sendString = true;
  @Output() selectedType = new EventEmitter<any>();

  dropdownForm = new FormControl<string>(
    '',
    this.required ? { validators: [Validators.required] } : undefined
  );
  propertyTypeValues: ComplexItem[] = [];
  complexData: ComplexItem[] = [];
  backUpComplexData: ComplexItem[] = [];
  translations: any = {};
  customTemplate = (item: ComplexItem) => `<span>${item.name}</span>`;
  formatForInput = (item: any) => (item ? item.name : '');
  filterKey: keyof ComplexItem = 'name';
  message: string | null = null;
  dropdownState: ErrorState | null = null;
  selectedItem: ComplexItem | null = null;
  public cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.initDataAndForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dropdownDataList'] && !changes['dropdownDataList'].firstChange) {
      // When the list changes (e.g., country changes), reload the dropdown data
      this.initDataAndForm();
    }
    if (changes['selectedValue'] && !changes['selectedValue'].firstChange) {
      const next = changes['selectedValue'].currentValue ?? '';
      this.setControlValue(next);
      this.syncSelectedItem(next);
      this.updateStatus();
      this.cdr.markForCheck();
    }
    if (changes['isSubmitted']?.currentValue === true) {
      this.showErrorWarning();
      this.cdr.markForCheck();
    }
    if (changes['required'] && !changes['required'].firstChange) {
      this.dropdownForm.setValidators(
        changes['required'].currentValue ? [Validators.required] : []
      );
      this.dropdownForm.updateValueAndValidity({ emitEvent: false });
      this.cdr.markForCheck();
    }
  }

  private initDataAndForm(): void {
    this.loadPropertyTypes();
    this.dropdownForm.statusChanges.subscribe(() => this.updateStatus());
    if (this.selectedValue) {
      this.setControlValue(this.selectedValue);
      this.syncSelectedItem(this.selectedValue);
    }
    if (this.disabled) {
      this.dropdownForm.disable({ emitEvent: false });
    } else {
      this.dropdownForm.enable({ emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  private loadPropertyTypes(): void {
    if (!this.useDifferentSet) {
      this.propertyTypeValues = dropdownData;
    } else {
      this.propertyTypeValues = this.dropdownDataList;
    }
    this.backUpComplexData = [...this.propertyTypeValues];
    this.complexData = this.propertyTypeValues;
  }

  private setControlValue(value: string): void {
    this.dropdownForm.setValue(value, { emitEvent: false });
  }

  private syncSelectedItem(value: string): void {
    const found = this.complexData.find((e) => e.name === value) ?? null;
    this.selectedItem = found;
  }

  updateStatus(): void {
    if (!this.showErrors) return;
    const found = this.complexData.find(
      (element) => element.name === this.dropdownForm.value
    );
    if (this.dropdownForm.valid && found) {
      this.message = null;
      this.dropdownState = null;
    } else {
      if (this.dropdownForm.touched) {
        this.showErrorWarning();
      }
    }
  }

  showErrorWarning(): void {
    if (!this.dropdownForm.value && !this.message) {
      this.message = `${this.labelName} is required.`;
    }
    if (this.message) {
      this.dropdownState = 'warning';
      if (this.isSubmitted) {
        this.dropdownState = 'error';
      }
    }
  }

  onAppointmentTypeSelect(event: Event): void {
    const { detail }: CustomEvent = event as CustomEvent;
    if (!detail) {
      this.dropdownState = null;
      this.selectedType.emit(null);
      return;
    }
    const nextName: string = detail.name;
    this.dropdownState = null;
    this.setControlValue(nextName);
    this.syncSelectedItem(nextName);
    if (this.sendString) {
      this.selectedType.emit([nextName]);
      this.cdr.markForCheck();
    } else {
      this.selectedType.emit(detail);
      this.cdr.markForCheck();
    }
  }

  onBlur(): void {
    if (this.required) {
      this.message = null;
      this.dropdownState = null;
      this.showErrorWarning();
      this.showInvalid();
      this.cdr.markForCheck();
    }
  }

  showInvalid(): void {
    const found = this.complexData.find(
      (element) => element.name === this.dropdownForm.value
    );
    if (!found && this.dropdownForm.value) {
      this.message = 'Invalid input';
      this.dropdownState = 'warning';
      this.selectedType.emit(null);
    }
    if (!found && !this.dropdownForm.value) {
      this.message = `${this.labelName} is required.`;
      this.dropdownState = this.isSubmitted ? 'error' : 'warning';
      this.selectedType.emit(null);
    }
  }

  resetData(): void {
    this.initDataAndForm();
  }
}
